import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  useWaitingRoomFilters,
  useWaitingRoomFiltersContext,
} from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import { ServiceSpecialty, ServiceSpecialtyValues } from "@/types/__generated__/union-types"
import { UI_DISPLAY_LANGUAGES } from "@/types/UiDisplayLanguage"

const SpecialtiesFilter = () => {
  const { t } = useTranslation()

  const { filters } = useWaitingRoomFilters()
  const setFilters = useWaitingRoomFiltersContext((state) => state.setFilters)

  const specialties = filters.specialties || []
  const specialtyCount = specialties.length

  // Sorted by the German label, so the order stays the same whatever the UI language is
  const sortedSpecialtyOptions = ServiceSpecialtyValues.map((e) => ({
    value: e,
    label: t(`scheduling.waitingRoom.filters.serviceSpecialtyFilter.${e}`),
    labelGerman: t(`scheduling.waitingRoom.filters.serviceSpecialtyFilter.${e}`, { lng: UI_DISPLAY_LANGUAGES.GERMAN }),
  })).toSorted((e1, e2) => {
    if (e1.value === "NONE") {
      return -1
    } else if (e2.value === "NONE") {
      return 1
    } else {
      return e1.labelGerman.localeCompare(e2.labelGerman)
    }
  })

  const handleCheckChange = (item: ServiceSpecialty) => {
    const index = specialties.indexOf(item)
    const newSpecialties = [...specialties]

    if (index < 0) newSpecialties.push(item)
    else newSpecialties.splice(index, 1)

    setFilters({ specialties: newSpecialties.length > 0 ? newSpecialties : null })
  }

  const handleResetSpecialties = () => {
    setFilters({ specialties: null })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer">
        <Chip data-testid="specialty-filter-trigger" className="h-8 gap-0.5" checked={specialtyCount > 0} type="button">
          <div className={`overflow-hidden transition-all duration-200 ${specialtyCount > 0 ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">
            {t("scheduling.waitingRoom.filters.serviceSpecialty") + (specialtyCount > 0 ? ` (${specialtyCount})` : ``)}
          </span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="specialty-filter-content"
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        <div className="grid gap-0.5">
          {sortedSpecialtyOptions.map((item) => (
            <DropdownMenuCheckboxItem
              onSelect={(e) => e.preventDefault()}
              className="h-9"
              key={item.value}
              checked={specialties.includes(item.value)}
              data-testid={`menu-specialty-${item.value.toLowerCase()}`}
              onCheckedChange={() => handleCheckChange(item.value)}>
              {item.label}
            </DropdownMenuCheckboxItem>
          ))}
        </div>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={handleResetSpecialties}
            data-testid="reset-specialty-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { SpecialtiesFilter }
